import { vNodeUnmount } from './vNode.unmount';
import { vNodeMount } from './vNode.mount';
import { isNotNullOrUndef } from '../../utils/isNotNullOrUndef';
import { isNullOrUndef } from '../../utils/isNullOrUndef';
import { addElementToDOM } from './utils/addElementToDOM';
import { elementEventListener } from './utils/elementEventListener';
import { setAttributeToElement } from './utils/setAttributeToElement';
import { isEqual } from '../../js/helpers/isEqual';
import { vNodeReplaceElement } from './vNode.replaceElement';
import { dispatchWillUnmount } from './utils/dispatch.willUnmount';
import { dispatchDidUnmount } from './utils/dispatch.didUnmount';
import { dispatchDidMount } from './utils/dispatch.didMount';
const patchProps = (oldVNode, newVNode) => {
    const el = newVNode._el;
    if (isNullOrUndef(el))
        return;
    oldVNode.propsArray.forEach(({ key }) => {
        if (isNullOrUndef(newVNode.props[key])) {
            el.removeAttribute(key);
        }
    });
    newVNode.propsArray.forEach(({ key, value }) => {
        if (value === oldVNode.props[key])
            return;
        setAttributeToElement(el, newVNode.tag, key, value);
    });
    oldVNode.hiddenPropsArray.forEach(({ key }) => {
        if (isNullOrUndef(newVNode.hiddenProps[key]) && key in el) {
            el[key] = undefined;
        }
    });
    newVNode.hiddenPropsArray.forEach(({ key, value }) => {
        if (!isEqual(value, oldVNode.hiddenProps[key])) {
            el[key] = value;
        }
    });
};
const patchEvents = (oldVNode, newVNode) => {
    const el = newVNode._el;
    if (isNullOrUndef(el))
        return;
    oldVNode.eventsArray.forEach(({ key }) => {
        if (isNullOrUndef(newVNode.events[key]) && key in el) {
            el[key] = undefined;
            el.removeEventListener(key, elementEventListener);
        }
    });
    newVNode.eventsArray.forEach(({ key, event }) => {
        el[key] = event;
        if (isNullOrUndef(oldVNode.events[key])) {
            el.addEventListener(key, elementEventListener);
        }
    });
};
const patch = (oldVNode, newVNode, container) => {
    if (isNotNullOrUndef(oldVNode._el)) {
        oldVNode = oldVNode._el._vNode;
    }
    if (oldVNode.type === 'STRING' && newVNode.type === 'STRING') {
        if (oldVNode.text === newVNode.text) {
            newVNode._el = oldVNode._el;
            if (isNotNullOrUndef(newVNode._el))
                newVNode._el._vNode = newVNode;
        }
        else {
            vNodeReplaceElement(oldVNode, newVNode, container);
        }
        return;
    }
    if (oldVNode.type === 'HTML' && newVNode.type === 'HTML') {
        if (oldVNode.html === newVNode.html) {
            newVNode._el = oldVNode._el;
            if (isNotNullOrUndef(newVNode._el))
                newVNode._el._vNode = newVNode;
        }
        else {
            vNodeReplaceElement(oldVNode, newVNode, container);
        }
        return;
    }
    if (oldVNode.type === 'ELEMENT' &&
        newVNode.type === 'ELEMENT' &&
        oldVNode.tag === newVNode.tag) {
        if (oldVNode.key !== newVNode.key) {
            dispatchWillUnmount(oldVNode);
        }
        else {
            oldVNode.lifeEvents.willUpdate?.(oldVNode._el);
        }
        newVNode._el = oldVNode._el;
        if (isNotNullOrUndef(newVNode._el))
            newVNode._el._vNode = newVNode;
        patchProps(oldVNode, newVNode);
        patchEvents(oldVNode, newVNode);
        if (oldVNode.children.length > 0 && newVNode.children.length > 0) {
            compareChildren(oldVNode.children, newVNode.children, newVNode._el);
        }
        else if (oldVNode.children.length > 0) {
            oldVNode.children.forEach(vNodeUnmount);
        }
        else if (newVNode.children.length > 0) {
            newVNode.children.forEach(vNode => { vNodeMount({ vNode, container: newVNode._el }); });
        }
        if (oldVNode.key !== newVNode.key) {
            dispatchDidUnmount(oldVNode);
            dispatchDidMount(newVNode);
        }
        else {
            newVNode.lifeEvents.didUpdate?.(newVNode._el);
        }
        return;
    }
    vNodeReplaceElement(oldVNode, newVNode, container);
};
const missedKey = (vNodes) => {
    return vNodes.some(vNode => vNode.type !== 'ELEMENT' || isNullOrUndef(vNode.key));
};
const compareChildrenWithoutKeys = (oldVNodes, newVNodes, container) => {
    const oldLength = oldVNodes.length;
    const newLength = newVNodes.length;
    const maxLength = Math.min(oldLength, newLength);
    for (let i = 0; i < maxLength; i++) {
        patch(oldVNodes[i], newVNodes[i], container);
    }
    if (oldLength > newLength) {
        for (let i = maxLength; i < oldLength; i++) {
            vNodeUnmount(oldVNodes[i]);
        }
    }
    else if (newLength > oldLength) {
        for (let i = maxLength; i < newLength; i++) {
            vNodeMount({ vNode: newVNodes[i], container });
        }
    }
};
const compareChildrenWithKeys = (oldVNodes, newVNodes, container) => {
    const oldKeys = oldVNodes.map(({ key }) => key);
    const newKeys = newVNodes.map(({ key }) => key);
    const freeOldIndexes = oldKeys.reduce((arr, key, i) => {
        if (!newKeys.includes(key)) {
            arr.push(i);
        }
        return arr;
    }, []);
    let fSI = 0;
    let fEI = freeOldIndexes.length - 1;
    let oSI = 0;
    let oEI = oldVNodes.length - 1;
    let nSI = 0;
    let nEI = newVNodes.length - 1;
    let oSK;
    let oEK;
    let nSK;
    let nEK;
    while (oSI <= oEI && nSI <= nEI) {
        oSK = oldKeys[oSI];
        oEK = oldKeys[oEI];
        nSK = newKeys[nSI];
        nEK = newKeys[nEI];
        if (oSK === undefined) {
            oSI++;
        }
        else if (oEK === undefined) {
            oEI--;
        }
        else if (oSK === nSK) {
            patch(oldVNodes[oSI], newVNodes[nSI], container);
            oSI++;
            nSI++;
        }
        else if (oEK === nEK) {
            patch(oldVNodes[oEI], newVNodes[nEI], container);
            oEI--;
            nEI--;
        }
        else if (oSK === nEK) {
            patch(oldVNodes[oSI], newVNodes[nEI], container);
            addElementToDOM({ vNode: newVNodes[nEI], container, nextEl: newVNodes[nEI + 1]?._el });
            oSI++;
            nEI--;
        }
        else if (oEK === nSK) {
            patch(oldVNodes[oEI], newVNodes[nSI], container);
            addElementToDOM({ vNode: newVNodes[nSI], container, prevEl: newVNodes[nSI - 1]?._el, nextEl: oldVNodes[oSI]?._el });
            oEI--;
            nSI++;
        }
        else if (!oldKeys.includes(nSK)) {
            let oldFreeIndex = -1;
            if (fSI <= fEI) {
                oldFreeIndex = freeOldIndexes[fSI++];
            }
            if (oldFreeIndex !== -1) {
                patch(oldVNodes[oldFreeIndex], newVNodes[nSI], container);
                if (oldFreeIndex !== oSI) {
                    addElementToDOM({ vNode: newVNodes[nSI], container, prevEl: newVNodes[nSI - 1]?._el, nextEl: oldVNodes[oSI]?._el });
                }
                oldKeys[oldFreeIndex] = undefined;
            }
            else {
                vNodeMount({ vNode: newVNodes[nSI], container, prevEl: newVNodes[nSI - 1]?._el, nextEl: oldVNodes[oSI]?._el });
            }
            nSI++;
        }
        else if (!oldKeys.includes(nEK)) {
            let oldFreeIndex = -1;
            if (fSI <= fEI) {
                oldFreeIndex = freeOldIndexes[fEI--];
            }
            if (oldFreeIndex !== -1) {
                patch(oldVNodes[oldFreeIndex], newVNodes[nEI], container);
                if (oldFreeIndex !== oEI) {
                    addElementToDOM({ vNode: newVNodes[nEI], container, nextEl: newVNodes[nEI + 1]?._el });
                }
                oldKeys[oldFreeIndex] = undefined;
            }
            else {
                vNodeMount({ vNode: newVNodes[nEI], container, nextEl: oldVNodes[nEI + 1]?._el });
            }
            nEI--;
        }
        else {
            const oldIndex = oldKeys.indexOf(nSK);
            patch(oldVNodes[oldIndex], newVNodes[nSI], container);
            addElementToDOM({ vNode: newVNodes[nSI], container, prevEl: newVNodes[nSI - 1]?._el, nextEl: oldVNodes[oSI]?._el });
            nSI++;
            oldKeys[oldIndex] = undefined;
        }
    }
    if (oSI > oEI) {
        for (; nSI <= nEI; nSI++) {
            vNodeMount({ vNode: newVNodes[nSI], container, prevEl: newVNodes[nSI - 1]?._el, nextEl: newVNodes[nEI + 1]?._el });
        }
    }
    else if (nSI > nEI) {
        for (; oSI <= oEI; oSI++) {
            oldKeys[oSI] !== undefined && vNodeUnmount(oldVNodes[oSI]);
        }
    }
};
const compareChildren = (oldVNodes, newVNodes, container) => {
    if (missedKey(oldVNodes) || missedKey(newVNodes)) {
        compareChildrenWithoutKeys(oldVNodes, newVNodes, container);
    }
    else {
        compareChildrenWithKeys(oldVNodes, newVNodes, container);
    }
};
export const vNodePatch = (oldVNode, newVNode, container) => {
    if (oldVNode instanceof Array || newVNode instanceof Array) {
        const oldIsNotEmpty = oldVNode instanceof Array && oldVNode.length > 0;
        const newIsNotEmpty = newVNode instanceof Array && newVNode.length > 0;
        if (oldIsNotEmpty && newIsNotEmpty) {
            compareChildren(oldVNode, newVNode, container);
        }
        else if (oldIsNotEmpty) {
            oldVNode.forEach(vNodeUnmount);
        }
        else if (newIsNotEmpty) {
            newVNode.forEach(vNode => { vNodeMount({ vNode, container }); });
        }
        return;
    }
    const oldIsNotNull = isNotNullOrUndef(oldVNode);
    const newIsNotNull = isNotNullOrUndef(newVNode);
    if (oldIsNotNull && newIsNotNull) {
        patch(oldVNode, newVNode, container);
    }
    else if (oldIsNotNull) {
        vNodeUnmount(oldVNode);
    }
    else if (newIsNotNull) {
        vNodeMount({ vNode: newVNode, container });
    }
};
